import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { HomepageCollectionSection, HomepageExhibition } from '../../types';
import { ArtworkObjectImage } from '../artwork/ArtworkObjectImage';
import { artworkTileStyles } from '../../styles/artworkTile';

interface Props {
  exhibitions: HomepageExhibition[];
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  availability?: HomepageCollectionSection;
  onRetry?: () => void;
}

const Section = styled.section`
  background: ${({ theme }) => theme.colors.gallery.surface};
  padding: clamp(3rem, 8vw, 6rem) 0;
  border-top: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (min-width: 768px) {
    padding: 0 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.gallery.section};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  margin: 0;

  @media (min-width: 768px) {
    font-size: 28px;
  }
`;

const AllLink = styled(Link)`
  margin-left: auto;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.gallery.accent.primary};
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.gallery.accent.hover};
  }
`;

const MobileScrollTrack = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 768px) {
    display: none;
  }
`;

const DesktopGrid = styled.div`
  display: none;

  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  @media (min-width: 1025px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1440px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
  }
`;

const Card = styled(Link)<{ $mobile?: boolean }>`
  ${({ $mobile }) => $mobile
    ? 'flex: 0 0 230px;'
    : ''}
  background: ${({ theme }) => theme.colors.gallery.surfaceRaised};
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  text-decoration: none;
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
  transition:
    border-color ${({ theme }) => theme.transitions.gallery.smooth},
    transform ${({ theme }) => theme.transitions.gallery.smooth},
    box-shadow ${({ theme }) => theme.transitions.gallery.smooth};

  &:hover {
    border-color: ${({ theme }) => theme.colors.gallery.accent.border};
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.gallery.artwork.museum};
  }
`;

const CardBody = styled.div`
  padding: 0.875rem;
`;

const CardTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.heading};
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gallery.text.primary};
  margin: 0 0 0.625rem;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ThumbnailRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
  margin-bottom: 0.625rem;
`;

const Thumb = styled.div`
  ${artworkTileStyles}
  aspect-ratio: 1;
  padding: 4px;
  box-shadow: none;
`;

const ThumbPlaceholder = styled.div`
  aspect-ratio: 1;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.gallery.mat};
`;

const DateLabel = styled.span`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.colors.gallery.text.muted};
`;

const shimmerBase = `
  background: linear-gradient(90deg, ${theme.colors.semantic.surface} 25%, ${theme.colors.semantic.surfaceRaised} 50%, ${theme.colors.semantic.surface} 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  @keyframes shimmer {
    from { background-position: 200% 0; }
    to { background-position: -200% 0; }
  }
`;

const MobileSkeletonCard = styled.div`
  ${shimmerBase}
  flex: 0 0 230px;
  height: 180px;
  border-radius: 12px;
`;

const DesktopSkeletonCard = styled.div`
  ${shimmerBase}
  height: 180px;
  border-radius: 12px;
`;

const CollectionStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.gallery.border.subtle};
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  color: ${({ theme }) => theme.colors.gallery.text.secondary};
  font-size: 0.875rem;

  @media (max-width: 560px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const RetryButton = styled.button`
  min-height: 44px;
  padding: 0 0.9rem;
  border: 1px solid ${({ theme }) => theme.colors.semantic.borderStrong};
  border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
  background: ${({ theme }) => theme.colors.semantic.surfaceRaised};
  color: ${({ theme }) => theme.colors.semantic.textPrimary};
  font: inherit;
  cursor: pointer;
`;

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

function ExhibitionCard({
  exhibition,
  mobile,
}: {
  exhibition: HomepageExhibition;
  mobile?: boolean;
}) {
  const thumbArtworks = (exhibition.artworks || []).slice(0, 4);
  const thumbCount = thumbArtworks.length;

  return (
    <Card $mobile={mobile} to={`/exhibitions?selected=${exhibition.exhibition_id}`}>
      <CardBody>
        <CardTitle>{exhibition.title}</CardTitle>
        <ThumbnailRow>
          {thumbArtworks.map((artwork) => (
            <Thumb key={artwork.artwork_id}>
              <ArtworkObjectImage
                artworkId={artwork.artwork_id}
                variant="thumbnail"
                updatedAt={artwork.updated_at}
                alt=""
              />
            </Thumb>
          ))}
          {Array.from({ length: Math.max(0, 4 - thumbCount) }).map((_, i) => (
            <ThumbPlaceholder key={`ph-${i}`} />
          ))}
        </ThumbnailRow>
        <DateLabel>{formatDate(exhibition.created_at)}</DateLabel>
      </CardBody>
    </Card>
  );
}

export function ExhibitionsStrip({
  exhibitions,
  isLoading,
  isError = false,
  errorMessage,
  availability,
  onRetry,
}: Props) {
  const collectionUnavailable = isError || availability?.status === 'unavailable';

  if (isLoading) {
    const skeletons = Array.from({ length: 4 }, (_, i) => i);
    return (
      <Section>
        <Inner>
          <Header>
            <Title>Exhibitions</Title>
            <AllLink to="/exhibitions">All &rarr;</AllLink>
          </Header>
          <MobileScrollTrack>
            {skeletons.map((i) => <MobileSkeletonCard key={i} />)}
          </MobileScrollTrack>
          <DesktopGrid>
            {skeletons.map((i) => <DesktopSkeletonCard key={i} />)}
          </DesktopGrid>
        </Inner>
      </Section>
    );
  }

  return (
    <Section>
      <Inner>
        <Header>
          <Title>Exhibitions</Title>
          <AllLink to="/exhibitions">All &rarr;</AllLink>
        </Header>
        {collectionUnavailable && (
          <CollectionStatus role="alert">
            <span>{errorMessage || availability?.message || 'Collection previews are temporarily unavailable.'}</span>
            {onRetry && <RetryButton type="button" onClick={onRetry}>Try again</RetryButton>}
          </CollectionStatus>
        )}

        {exhibitions.length === 0 && !collectionUnavailable && (
          <CollectionStatus role="status">
            <span>No exhibition previews are available right now.</span>
          </CollectionStatus>
        )}

        <MobileScrollTrack>
          {exhibitions.map((ex) => (
            <ExhibitionCard key={ex.exhibition_id} exhibition={ex} mobile />
          ))}
        </MobileScrollTrack>

        <DesktopGrid>
          {exhibitions.map((ex) => (
            <ExhibitionCard key={ex.exhibition_id} exhibition={ex} />
          ))}
        </DesktopGrid>
      </Inner>
    </Section>
  );
}
